// src/components/RefundCreateModal.js
// « Nouveau remboursement » from the refunds page (KI-158: a refund could only
// be typed inside a ticket's side tab). Step 1 finds the ticket by an exact
// reference — ticket id, external ticket id, tracking number or order — and
// shows what the refund will be tied to (client, order, carrier). Step 2 is
// the shared RefundForm, prefilled with the ticket's declared values as
// one-click suggestions.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Banknote, ChevronRight, Plus, Search, Ticket } from 'lucide-react';
import RefundForm from './RefundForm';
import { Badge, Banner, IconChip, Modal } from '../design/ui';
import { newRefundForm, refundPayload } from '../utils/refunds';
import './Refunds.css';

const ticketTitle = (ticket, t) => t('refunds.ticketRef', 'Ticket {{id}}', {
  id: ticket.external_ticket_id || String(ticket.ticket_id || '').slice(-8),
});

const ticketMeta = (ticket, t) => [
  ticket.client_name,
  ticket.order_reference && t('refunds.orderShort', 'Cde {{order}}', { order: ticket.order_reference }),
  ticket.carrier_name,
  ticket.tracking_number,
].filter(Boolean).join(' · ') || '—';

const RefundCreateModal = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` }), []);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null); // null = no search yet
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const close = () => { if (!saving) onClose(); };

  const pick = (ticket) => {
    setSelected(ticket);
    setForm(newRefundForm(ticket));
    setError('');
  };

  const search = async (event) => {
    event?.preventDefault();
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setError('');
    try {
      const response = await axios.get('/api/logistics/refunds/ticket-lookup', { headers, params: { q } });
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      setResults(items);
      if (items.length === 1) pick(items[0]);
    } catch (err) {
      console.error('Refund ticket lookup failed:', err);
      setError(err?.response?.data?.detail || t('refunds.lookupError', 'La recherche a échoué.'));
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!selected || !form) return;
    setSaving(true);
    setError('');
    try {
      const response = await axios.post(`/api/logistics/tickets/${selected.ticket_id}/refunds`, refundPayload(form), { headers });
      onCreated(response.data);
    } catch (err) {
      console.error('Failed to create refund:', err);
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('refunds.createError', 'Impossible de créer le remboursement.'));
      setSaving(false);
    }
  };

  const footer = selected ? (
    <>
      <button type="button" className="u-btn u-btn--secondary" onClick={close} disabled={saving}>
        {t('refunds.cancel', 'Annuler')}
      </button>
      <button type="submit" form="refund-create-form" className="u-btn u-btn--primary" disabled={saving}>
        <Plus aria-hidden="true" />
        {saving ? t('refunds.saving', 'Enregistrement…') : t('refunds.createRefund', 'Créer le remboursement')}
      </button>
    </>
  ) : null;

  return (
    <Modal
      title={t('refunds.createTitle', 'Enregistrer un remboursement')}
      description={selected
        ? t('ordersMoneyUi.create.stepForm', 'Étape 2 sur 2 · ce que le transporteur ou l’équipe accorde pour ce ticket.')
        : t('ordersMoneyUi.create.stepLookup', 'Étape 1 sur 2 · retrouvez le ticket auquel le remboursement se rattache.')}
      icon={Banknote}
      onClose={close}
      closeLabel={t('common.close', 'Fermer')}
      wide
      footer={footer}
    >
      {!selected ? (
        <div className="refund-create-v2">
          <form className="refund-create-v2__lookup" onSubmit={search}>
            <div className="u-field">
              <label className="u-label" htmlFor="rfm-lookup-q">
                {t('refunds.lookupLabel', 'Ticket, commande, numéro de suivi ou ID ticket externe')}
              </label>
              <div className="u-inline-form">
                <div className="u-input-group u-input-group--lead">
                  <Search className="u-input-group__icon" aria-hidden="true" />
                  <input
                    id="rfm-lookup-q"
                    ref={inputRef}
                    className="u-input"
                    type="text"
                    maxLength={100}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('refunds.lookupPlaceholder', 'ex. 1011751942, XW123456789FR, 217069')}
                  />
                </div>
                <button type="submit" className="u-btn u-btn--secondary" disabled={searching || !query.trim()}>
                  {searching ? t('refunds.lookupSearching', 'Recherche…') : t('refunds.lookupSearch', 'Rechercher')}
                </button>
              </div>
              <p className="u-help">{t('refunds.lookupHint', 'Correspondance exacte sur l’identifiant du ticket, l’ID externe, le numéro de suivi ou la référence de commande.')}</p>
            </div>
          </form>

          {error && <Banner tone="danger">{error}</Banner>}

          {results && results.length === 0 && !error && (
            <p className="u-muted refund-create-v2__empty">{t('refunds.lookupNoResult', 'Aucun ticket ne correspond exactement à cette référence.')}</p>
          )}
          {results && results.length > 0 && (
            <ul className="u-list refund-create-v2__results">
              {results.map((ticket) => (
                <li key={ticket.ticket_id}>
                  <button type="button" className="u-row refund-create-v2__result" onClick={() => pick(ticket)}>
                    <IconChip icon={Ticket} size="sm" tone="neutral" />
                    <span className="u-row__main">
                      <span className="u-row__title">
                        {ticketTitle(ticket, t)}
                        {ticket.refunds_count > 0 && (
                          <Badge tone="warning">{t('refunds.existingCount', '{{count}} remboursement(s) déjà', { count: ticket.refunds_count })}</Badge>
                        )}
                      </span>
                      <span className="u-row__meta">{ticketMeta(ticket, t)}</span>
                    </span>
                    <ChevronRight className="refund-create-v2__chevron" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <form id="refund-create-form" className="refund-create-v2" onSubmit={submit}>
          <div className="u-row refund-create-v2__selected">
            <IconChip icon={Ticket} size="sm" />
            <div className="u-row__main">
              <div className="u-row__title">{ticketTitle(selected, t)}</div>
              <div className="u-row__meta">{ticketMeta(selected, t)}</div>
            </div>
            <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => { setSelected(null); setForm(null); }} disabled={saving}>
              {t('ordersMoneyUi.create.changeTicket', 'Changer de ticket')}
            </button>
          </div>
          {selected.refunds_count > 0 && (
            <Banner tone="warning">
              {t('refunds.existingWarning', 'Ce ticket a déjà {{count}} remboursement(s) : vérifiez qu’il ne s’agit pas du même avant d’en créer un autre.', { count: selected.refunds_count })}
            </Banner>
          )}
          <RefundForm
            form={form}
            onChange={(field, value) => setForm((prev) => ({ ...prev, [field]: value }))}
            declared={selected.declared}
            ticketOrder={selected.order_reference}
            idPrefix="rfm-create"
          />
          {error && <Banner tone="danger">{error}</Banner>}
        </form>
      )}
    </Modal>
  );
};

export default RefundCreateModal;
